import React, { useContext, useCallback, useMemo, useEffect, useState } from 'react';
import clsx from 'clsx';
import { CircularProgress, makeStyles, Theme } from '@material-ui/core';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, setIsEditingForm, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { ApiError, RequestMethod, RobotCloudAPI, RobotDefinition } from '@moderepo/mode-apis';
import { fetchRobotCatalog, rcDataContext, selectRobotCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../../utils/customHooks';
import { RCRobotDefinitionInfo, RCRobotDefinitionInfoProps } from '../../../components/robot-cloud/admin/RCRobotDefinitionInfo';


const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface RobotDefinitionPreviewContainerProps {
    readonly projectId: number;
    readonly robotClass: string | undefined;
}


export const RCRobotDefinitionPreviewContainer = (props: RobotDefinitionPreviewContainerProps) => {
    const { projectId } = props;
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const isNewRobotDef = props.robotClass === undefined;
    const robotCatalog = selectRobotCatalog(rcDataState, projectId);
    const selectedRobotDef = props.robotClass && robotCatalog ? robotCatalog.find((robotDef: RobotDefinition) => {
        return robotDef.robotClass === props.robotClass;
    }) : undefined;
    const classes = useStyle();


    const [isEditing, setIsEditing] = useState<boolean>();
    const [inputError, setInputError] = useState<string | undefined>();


    const loadRobotCatalog = useCallback(() => {
        rcDataDispatch(fetchRobotCatalog(projectId)).catch(() => { /* ignore error */ });
    }, [projectId, rcDataDispatch]);


    /**
     * This useEffect used for loading Robot catalog on component init
     */
    useEffect(() => {
        if (!robotCatalog) {
            loadRobotCatalog();
        }
    }, [props.robotClass, selectedRobotDef, robotCatalog, projectId, loadRobotCatalog]);


    /**
     * Unselect the selected Robot definition and update the URL
     */
    const unselectRobotDefinition = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputError(undefined);

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (isNewRobotDef) {
            unselectRobotDefinition();
        }
    }, [closeEditing, isNewRobotDef, unselectRobotDefinition]);



    const onRemoveRobotDefActionClicked = useCallback((robotDef: RobotDefinition) => {
        // Confirm before removing home
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'Remove Robot Definition',
            message       : 'Are you sure you want to remove the robot definition for robotClass \'{{robot_class}}\'',
            messageData   : { robot_class: robotDef.robotClass },
            positiveButton: 'label.remove',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    RobotCloudAPI.getInstance().sendRequest(
                        RequestMethod.DELETE,
                        `/projects/${projectId}/appProxy/rcp/robotDefinitions/${robotDef.robotClass}`,
                    ).then(() => {
                        uiDispatch(showSuccessNotification('Robot Catalog Deleted successfully'));
                        closeEditing();
                        unselectRobotDefinition();

                        // Refetch Robot catalog
                        loadRobotCatalog();
                    }).catch((error: ApiError) => {
                        uiDispatch(showErrorNotification(error.message));
                    });
                }
            },
        }));
    }, [closeEditing, loadRobotCatalog, projectId, uiDispatch, unselectRobotDefinition]);



    const onFormSubmit = useCallback((inputs: {
        readonly robotDef: string | undefined
    }) => {
        const { robotDef } = inputs;

        if (!robotDef) {
            setInputError('Robot definition is required');
        } else {

            try {
                // try to parse robotDef to make sure it is valid JSON
                const robotDefJSON = JSON.parse(robotDef);

                if (isNewRobotDef) {
                    // Create new Robot definition
                    RobotCloudAPI.getInstance().sendRequest(
                        RequestMethod.POST,
                        `/projects/${projectId}/appProxy/rcp/robotDefinitions`, robotDefJSON,
                    ).then(() => {
                        uiDispatch(showSuccessNotification('Robot Catalog created successfully'));
                        closeEditing();

                        unselectRobotDefinition();

                        // Refetch Robot catalog
                        loadRobotCatalog();
                    }).catch((error: ApiError) => {
                        uiDispatch(showErrorNotification(error.message));
                    });
                } else {
                    // Update existing Robot definition
                    RobotCloudAPI.getInstance().sendRequest(
                        RequestMethod.PATCH,
                        `/projects/${projectId}/appProxy/rcp/robotDefinitions/${selectedRobotDef?.robotClass}`, robotDefJSON,
                    ).then(() => {
                        uiDispatch(showSuccessNotification('Robot Definition saved successfully'));
                        closeEditing();

                        // Refetch Robot catalog
                        loadRobotCatalog();
                    }).catch((error: ApiError) => {
                        uiDispatch(showErrorNotification(error.message));
                    });
                }
            } catch (error) {
                setInputError((error as Error).message);
            }
        }
    }, [closeEditing, isNewRobotDef, loadRobotCatalog, projectId, selectedRobotDef, uiDispatch, unselectRobotDefinition]);



    const robotDefinitionInfoSettings = useMemo((): RCRobotDefinitionInfoProps['fields'] => {
        return {
            robotDef: {
                label   : 'Robot Definition',
                editable: true,
                value   : selectedRobotDef ? JSON.stringify({
                    ...selectedRobotDef,
                    createdAt     : undefined,
                    createdBy     : undefined,
                    updatedAt     : undefined,
                    createdAgentId: undefined,
                    updatedAgentId: undefined,
                }, undefined, 4) : undefined,
                error: inputError || '',
            },
        };
    }, [inputError, selectedRobotDef]);



    const secondaryActions = useMemo(() => {
        if (selectedRobotDef) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : 'Remove Robot Definition',
                onClick  : () => {
                    onRemoveRobotDefActionClicked(selectedRobotDef);
                },
            }];
            return actions;
        }
        return undefined;
    }, [classes.secondaryAction, onRemoveRobotDefActionClicked, selectedRobotDef]);


    /**
     * Validate the value string and make sure it is a valid JSON, reformat the string and return the formatted string using 4 spaces tab.
     * Throw an error if the JSON is invalid.
     */
    const onValidateJSON = useCallback((value: string): string => {
        return JSON.stringify(JSON.parse(value), undefined, 4);
    }, []);


    const slideOutCompContent = useMemo(() => {
        if (isNewRobotDef || selectedRobotDef) {
            return (
                <RCRobotDefinitionInfo
                    className={clsx(isEditing && 'editing')}
                    title="Robot Definition"
                    icon="router"
                    fields={robotDefinitionInfoSettings}
                    isEditing={isEditing}
                    actions={{
                        formatJson: {
                            label  : capitalizeAll(trans('label.format_json')),
                            onClick: onValidateJSON,
                        },
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : isNewRobotDef ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                            onClick: onFormSubmit,
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={() => {
                        setInputError(undefined);
                    }}
                />
            );
        }

        return (
            <CircularProgress />
        );
    }, [isEditing, isNewRobotDef, onCancelEdit, onChangeEditingMode, onFormSubmit, onValidateJSON, robotDefinitionInfoSettings, selectedRobotDef,
        trans]);


    return (
        <SlideOutComp
            title={selectedRobotDef?.robotClass}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
